import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { openCookieSettings } from '../components/CookieConsent';
import { platformAPI } from '../lib/api';
import { 
  TrendingUp, Shield, RefreshCw, DollarSign, 
  Target, Users, Award, ArrowRight, Check,
  ChevronRight, Star, Zap, BarChart3,
  Globe, Clock, Cookie, Sparkles, Coins, Trophy
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [memberCount, setMemberCount] = useState(1561);

  // Fetch platform settings and member count on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await platformAPI.getSettings();
        setPlatformSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch platform settings');
      }
    };
    
    const fetchMemberCount = async () => {
      try {
        const response = await platformAPI.getMemberCount();
        if (response.data?.count) {
          setMemberCount(response.data.count);
        }
      } catch (error) {
        // Use default count if API fails
        console.log('Using default member count');
      }
    };
    
    fetchSettings();
    fetchMemberCount();
  }, []);

  // Extract values from settings or use defaults
  const tradingRules = platformSettings?.trading_rules || {
    max_daily_loss: 5,
    max_total_loss: 10,
    max_floating_loss: 2,
    profit_target_phase1: 8,
    profit_target_phase2: 5
  };
  
  const payoutSettings = platformSettings?.payout_settings || {
    first_payout_days: 6,
    profit_split_base: 90,
    profit_split_upgraded: 100,
    processing_time_hours: 24
  };

  const heroSlides = [
    {
      title: "GET FUNDED",
      subtitle: "TRADE WITH CONFIDENCE",
      description: "Trade with up to $200,000 in capital. Keep up to 100% of your profits with scalable options.",
      cta: "Start Your Challenge",
      ctaLink: "/get-funded",
      highlight: "No Risk to Your Capital"
    },
    {
      title: "KBTCOIN",
      subtitle: "CRYPTO TOKEN LAUNCH",
      description: "Earn exclusive tokens with every Funded Instant purchase. Join the waitlist for early access to our seed round.",
      cta: "Learn More",
      ctaLink: "#kbtcoin-section",
      highlight: "Launching August 2027",
      isSpecial: true,
      specialColor: "yellow"
    },
    {
      title: "ELITE FUNDED",
      subtitle: "TRADER PROGRAM",
      description: "Trade with real capital under live market conditions. 80% profit split with 100% guaranteed payouts.",
      cta: "Discover More",
      ctaLink: "#elite-funded-section",
      highlight: "Selection-Based Access",
      isSpecial: true,
      specialColor: "green"
    },
    {
      title: "INSTANT FUNDING",
      subtitle: "SKIP THE EVALUATION",
      description: "Ready to trade now? Get funded immediately with our instant funding option.",
      cta: "Get Instant Funding",
      ctaLink: "/get-funded",
      highlight: "Start Trading Today"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: TrendingUp,
      title: 'Up to $200K Funding',
      description: 'Trade with institutional-grade capital without risking your own money.',
    },
    {
      icon: RefreshCw,
      title: 'Free Retakes',
      description: 'First-time buyers get a complimentary retake if they fail.',
    },
    {
      icon: Shield,
      title: 'Fair Trading Rules',
      description: `${tradingRules.max_total_loss}% max drawdown, ${tradingRules.max_daily_loss}% daily loss limit. Achievable targets.`,
    },
    {
      icon: DollarSign,
      title: `Up to ${payoutSettings.profit_split_upgraded}% Profit Split`,
      description: `Scalable options available. Withdraw after just ${payoutSettings.first_payout_days} trading days.`,
    },
    {
      icon: Clock,
      title: 'Fast Payouts',
      description: `First payout after ${payoutSettings.first_payout_days} trading days. Processed within ${payoutSettings.processing_time_hours} hours.`,
    },
    {
      icon: Globe,
      title: 'Trade Anywhere',
      description: 'Access your account from anywhere in the world, 24/7.',
    }
  ];

  const stats = [
    { value: memberCount.toLocaleString() + '+', label: 'Growing Community', icon: Users },
    { value: `${payoutSettings.profit_split_upgraded}%`, label: 'Max Profit Split', icon: TrendingUp },
    { value: `${payoutSettings.first_payout_days} Days`, label: 'First Payout Eligible', icon: Clock },
    { value: '100%', label: 'Guaranteed Payouts', icon: Shield }
  ];

  // Get pricing from platform settings or use defaults
  const pricingPlans = platformSettings?.packages
    ?.filter(pkg => pkg.type === 'challenge')
    ?.map(pkg => ({
      size: `$${(pkg.account_size / 1000).toFixed(0)}K`,
      price: `$${pkg.price}`,
      popular: pkg.account_size === 50000
    })) || [
    { size: '$10K', price: '$99', popular: false },
    { size: '$25K', price: '$199', popular: false },
    { size: '$50K', price: '$299', popular: true },
    { size: '$100K', price: '$499', popular: false },
    { size: '$200K', price: '$899', popular: false }
  ];

  const testimonials = [
    {
      name: "Michael Chen",
      role: "Funded Trader",
      quote: "Got funded within 2 weeks. The rules are fair and the support team is amazing.",
      rating: 5
    },
    {
      name: "Sarah Williams",
      role: "Professional Trader",
      quote: "Best prop firm I've worked with. Fast payouts and transparent rules.",
      rating: 5
    },
    {
      name: "David Martinez",
      role: "Funded Trader",
      quote: "The free retake saved me. Failed first time, passed second. Now trading $100K!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-hero-gradient" data-testid="hero-section">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-3xl" />
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-24 pb-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* LARGE HERO LOGO */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-8"
              >
                <img 
                  src="/logo.png" 
                  alt="Traders Capital" 
                  className="h-32 sm:h-40 lg:h-52 w-auto mx-auto drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 0 30px rgba(0, 149, 255, 0.3))' }}
                />
              </motion.div>

              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Badge className={`mb-6 px-5 py-2 backdrop-blur-sm text-sm font-medium ${
                  heroSlides[currentSlide].specialColor === 'yellow' 
                    ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                    : heroSlides[currentSlide].specialColor === 'green'
                    ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                    : 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
                }`} data-testid="hero-badge">
                  <Zap className={`w-4 h-4 mr-2 ${
                    heroSlides[currentSlide].specialColor === 'yellow' ? 'text-yellow-400' :
                    heroSlides[currentSlide].specialColor === 'green' ? 'text-green-400' : 'text-blue-400'
                  }`} />
                  {heroSlides[currentSlide].highlight}
                </Badge>
              </motion.div>
              
              {/* Title */}
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-black uppercase tracking-tight mb-4 text-white text-shadow" data-testid="hero-title">
                {heroSlides[currentSlide].title}
                <br />
                <span className={
                  heroSlides[currentSlide].specialColor === 'yellow' 
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent'
                    : heroSlides[currentSlide].specialColor === 'green'
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent'
                    : 'gradient-text'
                }>
                  {heroSlides[currentSlide].subtitle}
                </span>
              </h1>
              
              {/* Description */}
              <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
                {heroSlides[currentSlide].description}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => {
                    const link = heroSlides[currentSlide].ctaLink;
                    if (link.startsWith('#')) {
                      document.querySelector(link)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate(link);
                    }
                  }}
                  className={`text-lg px-10 py-7 font-heading uppercase tracking-wider rounded-full shadow-2xl transition-all hover:scale-105 ${
                    heroSlides[currentSlide].specialColor === 'yellow'
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 shadow-yellow-500/30 hover:shadow-yellow-500/40'
                      : heroSlides[currentSlide].specialColor === 'green'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-green-500/30 hover:shadow-green-500/40'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-blue-500/30 hover:shadow-blue-500/40'
                  } text-white`}
                  data-testid="hero-cta"
                >
                  {heroSlides[currentSlide].cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/get-funded')}
                  className="text-lg px-10 py-7 font-heading uppercase tracking-wider border-2 border-white/20 text-white hover:bg-white/5 hover:border-white/30 rounded-full backdrop-blur-sm"
                  data-testid="hero-secondary-cta"
                >
                  View Pricing
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-2 mt-12">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-blue-400 w-8' 
                    : 'bg-white/20 w-2 hover:bg-white/40'
                }`}
                data-testid={`slide-indicator-${index}`}
              />
            ))}
          </div>

          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronRight className="w-8 h-8 text-white/30 rotate-90" />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-[hsl(220,20%,6%)] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center stats-card"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <stat.icon className="w-7 h-7 text-blue-400" />
                </div>
                <p className="font-heading text-4xl lg:text-5xl font-black text-white mb-1">{stat.value}</p>
                <p className="text-gray-500 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Match-Trader Partnership Banner */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,6%)] border-y border-white/5" data-testid="partnership-banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Partnership Label */}
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 backdrop-blur-sm rounded-full px-5 py-2 mb-8 border border-cyan-500/30">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-300 text-sm font-medium">OFFICIAL TECHNOLOGY PARTNER</span>
            </div>
            
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              Powered by Industry-Leading Technology
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-12">
              We've partnered with Match-Trader to provide you with institutional-grade trading infrastructure, 
              ensuring lightning-fast execution and a seamless trading experience.
            </p>

            {/* Official Match-Trader Logo Section */}
            <div className="flex flex-wrap justify-center items-center gap-8 mb-12">
              <motion.a
                href="https://match-trader.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                className="block p-8 rounded-2xl bg-white border border-white/20 hover:border-cyan-500/50 transition-all shadow-2xl shadow-cyan-500/10"
              >
                {/* Official Match-Trader Logo */}
                <img 
                  src="https://match-trader.com/wp-content/uploads/2024/06/MTR_by_MTT.svg" 
                  alt="Match-Trader by Match-Trade Technologies" 
                  className="h-12 md:h-16 w-auto"
                  data-testid="match-trader-official-logo"
                />
              </motion.a>
            </div>

            {/* Technology Partner Badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm rounded-full px-6 py-3 mb-12 border border-cyan-500/20">
              <span className="text-cyan-400 font-medium">Official Technology Partner</span>
              <span className="text-gray-400">•</span>
              <span className="text-white text-sm">match-trader.com</span>
            </div>

            {/* Partnership Benefits */}
            <div className="grid md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-all"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-cyan-400" />
                </div>
                <p className="font-semibold text-white">Ultra-Fast Execution</p>
                <p className="text-gray-500 text-sm mt-1">Under 50ms latency</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-all"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <p className="font-semibold text-white">Award-Winning Platform</p>
                <p className="text-gray-500 text-sm mt-1">Industry recognized</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
                className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                </div>
                <p className="font-semibold text-white">TradingView Charts</p>
                <p className="text-gray-500 text-sm mt-1">Professional analytics</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                viewport={{ once: true }}
                className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-purple-400" />
                </div>
                <p className="font-semibold text-white">Global Access</p>
                <p className="text-gray-500 text-sm mt-1">Trade worldwide 24/7</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FREE RETAKE Hero Banner */}
      <section className="py-10 sm:py-16 bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-blue-600/20 relative overflow-hidden border-y border-blue-500/20" data-testid="free-retake-banner">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 bg-blue-500/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 border border-blue-500/30">
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-blue-300 font-bold text-lg">LIMITED TIME OFFER</span>
            </div>
            
            <h2 className="font-heading text-5xl lg:text-7xl font-black text-white mb-4 text-shadow-blue">
              FREE RETAKE
            </h2>
            <p className="text-2xl lg:text-3xl text-gray-300 font-bold mb-2">
              FOR ALL FIRST-TIME BUYERS
            </p>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              Failed your first attempt? Don't worry! Get another chance at <span className="font-bold text-cyan-400">NO EXTRA COST</span>. 
              We believe in giving traders the opportunity to succeed.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                onClick={() => navigate('/get-funded')}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase tracking-wider text-lg rounded-full px-10 py-7 shadow-2xl shadow-blue-500/30 transition-all hover:scale-105"
              >
                CLAIM YOUR FREE RETAKE
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2 text-gray-400">
                <Check className="w-5 h-5 text-green-400" />
                <span>No strings attached</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fast Payouts & Email Notifications Banner */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,4%)]" data-testid="payouts-banner">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Payout Options Preview */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 px-4 py-1 bg-green-500/10 text-green-400 border border-green-500/20">
                FLEXIBLE PAYOUT OPTIONS
              </Badge>
              <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
                GET PAID <span className="gradient-text">YOUR WAY</span>
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur rounded-xl border border-white/10 hover:border-blue-500/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Standard Payouts</p>
                    <p className="text-gray-400">First payout after 6 trading days, then every 14 days</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur rounded-xl border border-cyan-500/30 hover:border-cyan-400/50 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-cyan-400" />
                      Payout on Demand (+15%)
                    </p>
                    <p className="text-gray-400">Withdraw anytime you want - no waiting periods!</p>
                  </div>
                  <Badge className="bg-cyan-500 text-black font-bold shrink-0">UPGRADE</Badge>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur rounded-xl border border-white/10 hover:border-green-500/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Fast Processing</p>
                    <p className="text-gray-400">All withdrawal requests processed within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 backdrop-blur rounded-xl border border-yellow-500/30 hover:border-yellow-400/50 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                      <Target className="w-4 h-4 text-yellow-400" />
                      Minimum 6 Trading Days
                    </p>
                    <p className="text-gray-400">Must actively trade for at least 6 days before requesting payout</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Email Notifications Highlight */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-3xl blur-xl" />
              <div className="relative p-8 bg-[hsl(220,20%,7%)]/80 backdrop-blur-xl rounded-2xl border border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-white">EMAIL NOTIFICATIONS</h3>
                    <p className="text-blue-400 font-medium">Never Miss a Payout</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-400 shrink-0" />
                    <span>Get notified when you're eligible for your first payout</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-400 shrink-0" />
                    <span>Reminders when your next payout window opens</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-400 shrink-0" />
                    <span>Instant confirmation when withdrawal is processed</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-green-400 shrink-0" />
                    <span>Account milestone achievements & alerts</span>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                  <p className="text-blue-300 text-sm font-medium text-center">
                    SMS notifications coming soon!
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-24 bg-[hsl(220,20%,5%)]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
              WHY CHOOSE US
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              BUILT FOR <span className="gradient-text">SERIOUS TRADERS</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              We've created the most trader-friendly evaluation program in the industry.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="feature-card h-full border-0">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center mb-6">
                      <feature.icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-24 bg-[hsl(220,20%,4%)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 px-4 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              HOW IT WORKS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              3 SIMPLE <span className="gradient-text">STEPS</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Choose Your Challenge",
                description: "Select an account size from $10K to $200K. Pay once, trade forever.",
                icon: Target
              },
              {
                step: "02",
                title: "Prove Your Skills",
                description: "Reach the profit target while respecting drawdown limits. Take your time.",
                icon: BarChart3
              },
              {
                step: "03",
                title: "Get Funded",
                description: "Pass the evaluation and receive your funded account. Start earning.",
                icon: Award
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-center p-8 bg-[hsl(220,20%,7%)] rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all duration-300">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 flex items-center justify-center relative">
                    <item.icon className="w-9 h-9 text-blue-400" />
                    <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-heading font-bold text-sm flex items-center justify-center">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-blue-500/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-12 sm:py-24 bg-[hsl(220,20%,5%)]" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
              PRICING
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              CHOOSE YOUR <span className="gradient-text">ACCOUNT SIZE</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              One-time payment. No monthly fees. No hidden costs.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`relative overflow-hidden cursor-pointer ${
                  plan.popular 
                    ? 'pricing-card-featured' 
                    : 'pricing-card'
                }`}
                  onClick={() => navigate('/get-funded')}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <p className="font-heading text-3xl font-black mb-1 text-white">
                      {plan.size}
                    </p>
                    <p className="text-sm mb-4 text-gray-500">
                      Account Size
                    </p>
                    <p className={`font-heading text-2xl font-bold ${plan.popular ? 'text-cyan-400' : 'text-blue-400'}`}>
                      {plan.price}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              size="lg"
              onClick={() => navigate('/get-funded')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase tracking-wider rounded-full px-10 py-6 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all"
            >
              View All Options
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Program Comparison Table */}
      <section className="py-12 sm:py-24 bg-[hsl(220,20%,4%)]" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
              COMPARE OPTIONS
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              FIND YOUR <span className="gradient-text">PERFECT FIT</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Three funding paths. One goal. Choose what works for you.
            </p>
          </motion.div>

          {/* Comparison Cards (Mobile & Desktop) */}
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-10">
            {/* Evaluation Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-[hsl(220,20%,7%)] border border-blue-500/30 hover:border-blue-500/50 transition-all cursor-pointer" onClick={() => navigate('/get-funded')}>
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Target className="w-5 h-5 text-blue-400" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white">Evaluation</h3>
                    </div>
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">2 Phases</Badge>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Phase 1 Target</span>
                      <span className="text-white font-medium">{tradingRules.profit_target_phase1}%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Phase 2 Target</span>
                      <span className="text-white font-medium">{tradingRules.profit_target_phase2}%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Daily Drawdown</span>
                      <span className="text-white font-medium">{tradingRules.max_daily_loss}%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Max Drawdown</span>
                      <span className="text-white font-medium">{tradingRules.max_total_loss}%</span>
                    </div>
                    <div className="flex justify-between pb-2">
                      <span className="text-gray-400">Profit Split</span>
                      <span className="text-blue-400 font-bold">Up to 100%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 text-green-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Free Retake for first-timers</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-cyan-400 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>Unlimited Time</span>
                  </div>
                  <div className="text-center mt-4 text-xs text-gray-500">
                    From <span className="text-white font-bold">$99</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Instant Funded Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-[hsl(220,20%,7%)] border border-cyan-500/30 hover:border-cyan-500/50 transition-all cursor-pointer relative" onClick={() => navigate('/get-funded')}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <Coins className="w-3 h-3" />
                  +KBTCOIN
                </div>
                <CardContent className="p-5 sm:p-6 pt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-cyan-400" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white">Instant</h3>
                    </div>
                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs">No Evaluation</Badge>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Evaluation</span>
                      <span className="text-cyan-400 font-medium">None Required</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Daily Drawdown</span>
                      <span className="text-white font-medium">{tradingRules.max_daily_loss}%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Max Drawdown</span>
                      <span className="text-white font-medium">{tradingRules.max_total_loss}%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Profit Split</span>
                      <span className="text-cyan-400 font-bold">90%</span>
                    </div>
                    <div className="flex justify-between pb-2">
                      <span className="text-gray-400">100% Split</span>
                      <span className="text-green-400 font-medium">+25% fee</span>
                    </div>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2 mt-3">
                    <p className="text-yellow-400 text-xs text-center">$50K=10, $100K=20, $200K=40 KBTCOIN</p>
                  </div>
                  <div className="text-center mt-4 text-xs text-gray-500">
                    From <span className="text-white font-bold">$299</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Elite Funded Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="h-full bg-[hsl(220,20%,7%)] border border-green-500/30 hover:border-green-500/50 transition-all cursor-pointer relative" onClick={() => navigate('/get-funded')}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1">
                  <Badge className="bg-green-500 text-white text-xs">Real Capital</Badge>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">KBTCOIN TBC</Badge>
                </div>
                <CardContent className="p-5 sm:p-6 pt-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-green-400" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white">Elite</h3>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">Selection</Badge>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Access</span>
                      <span className="text-green-400 font-medium">Selection-Based</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Daily Drawdown</span>
                      <span className="text-white font-medium">10%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Max Drawdown</span>
                      <span className="text-white font-medium">20%</span>
                    </div>
                    <div className="flex justify-between border-b border-white/5 pb-2">
                      <span className="text-gray-400">Profit Split</span>
                      <span className="text-green-400 font-bold">80%</span>
                    </div>
                    <div className="flex justify-between pb-2">
                      <span className="text-gray-400">Payouts</span>
                      <span className="text-green-400 font-bold">100% Guaranteed</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 text-green-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>No 2% Floating Loss Rule</span>
                  </div>
                  <div className="text-center mt-4 text-xs text-gray-500">
                    From <span className="text-white font-bold">$4,999</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button 
              size="lg"
              onClick={() => navigate('/get-funded')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase tracking-wider rounded-full px-10 py-6 shadow-lg shadow-blue-500/25"
            >
              Compare All Options
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* KBTCOIN Crypto Section */}
      <section id="kbtcoin-section" className="py-12 sm:py-24 bg-gradient-to-b from-[hsl(220,20%,4%)] to-[hsl(220,20%,6%)] relative overflow-hidden" data-testid="kbtcoin-section">
        {/* Background glow */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 px-4 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                <Coins className="w-4 h-4 mr-2" />
                COMING AUGUST 2027
              </Badge>
              <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
                INTRODUCING <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">KBTCOIN</span>
              </h2>
              <p className="text-lg text-gray-400 mb-6">
                The exclusive utility token of Knowledge Based Trading ecosystem. 
                Earn tokens by purchasing Funded Instant accounts and benefit from our platform's growth.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Earn Tokens by Trading</p>
                    <p className="text-sm text-gray-500">$50K = 10 coins, $100K = 20 coins, $200K = 40 coins</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Value Tied to Business Growth</p>
                    <p className="text-sm text-gray-500">Token value increases with platform success</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Early Seed Round Access</p>
                    <p className="text-sm text-gray-500">Join the waitlist for exclusive pricing</p>
                  </div>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => navigate('/crypto')}
                className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-heading uppercase tracking-wider rounded-full px-8 py-6 shadow-lg shadow-yellow-500/25"
                data-testid="kbtcoin-learn-more"
              >
                Learn More
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-3xl blur-xl" />
              <div className="relative p-8 bg-[hsl(220,20%,7%)]/80 backdrop-blur-xl rounded-2xl border border-yellow-500/20">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
                  <Coins className="w-12 h-12 text-white" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-center text-white mb-2">KBTCOIN</h3>
                <p className="text-center text-gray-400 mb-6">Knowledge Based Trading Token</p>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-yellow-400">100M</p>
                    <p className="text-xs text-gray-500">Total Supply</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">10%</p>
                    <p className="text-xs text-gray-500">Seed Round</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">25%</p>
                    <p className="text-xs text-gray-500">Trader Rewards</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Elite Funded Trader Section */}
      <section id="elite-funded-section" className="py-12 sm:py-24 bg-[hsl(220,20%,5%)]" data-testid="elite-funded-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="relative p-8 bg-[hsl(220,20%,7%)]/80 backdrop-blur-xl rounded-2xl border border-green-500/20">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-3xl blur-xl -z-10" />
                
                <h3 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-green-400" />
                  Elite Trading Opportunity
                </h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400">Account Sizes</span>
                    <span className="text-white font-bold">$50K - $200K</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400">Profit Split</span>
                    <span className="text-green-400 font-bold">80% You / 20% Us</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400">Max Drawdown</span>
                    <span className="text-yellow-400 font-bold">20%</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-gray-400">Daily Drawdown</span>
                    <span className="text-yellow-400 font-bold">10%</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-400">Payouts</span>
                    <span className="text-green-400 font-bold">100% Guaranteed</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <Badge className="mb-4 px-4 py-1 bg-green-500/10 text-green-400 border border-green-500/20">
                <Trophy className="w-4 h-4 mr-2" />
                ELITE PROGRAM
              </Badge>
              <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
                ELITE FUNDED <span className="text-green-400">TRADER</span>
              </h2>
              <p className="text-lg text-gray-400 mb-6">
                For proven traders only. Demonstrate your skills on a Funded Instant account 
                and get selected to trade <span className="text-green-400 font-bold">real capital</span> with 
                <span className="text-green-400 font-bold"> 100% guaranteed payouts</span>.
              </p>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Trade with actual funded capital in live markets</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Keep 80% of all profits you generate</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Selection-based access for skilled traders</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>Every payout request is processed and guaranteed</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => navigate('/elite-funded')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-heading uppercase tracking-wider rounded-full px-8 py-6 shadow-lg shadow-green-500/25"
                data-testid="elite-funded-learn-more"
              >
                Learn More
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-24 bg-[hsl(220,20%,4%)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 px-4 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              TESTIMONIALS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              TRUSTED BY <span className="gradient-text">THOUSANDS</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border border-white/5 hover:border-blue-500/30 transition-all h-full">
                  <CardContent className="p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-6 italic">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-white">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-24 bg-gradient-to-b from-[hsl(220,20%,6%)] to-[hsl(220,20%,4%)] relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl lg:text-6xl font-black text-white mb-6 text-shadow">
              READY TO GET <span className="gradient-text">FUNDED?</span>
            </h2>
            <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
              Join thousands of traders who have already started their journey. 
              No experience required. Just prove your skills.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase tracking-wider text-lg rounded-full px-12 py-7 shadow-2xl shadow-blue-500/30 transition-all hover:scale-105 hover:shadow-blue-500/40 animate-pulse-blue"
              data-testid="cta-get-started"
            >
              START YOUR CHALLENGE NOW
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-[hsl(220,20%,3%)] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Traders Capital" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-gray-500 text-sm">
              <a href="/get-funded" className="hover:text-white transition-colors">Get Funded</a>
              <a href="/crypto" className="hover:text-yellow-400 transition-colors flex items-center gap-1">
                <Coins className="w-3 h-3" />
                KBTCOIN
              </a>
              <a href="/elite-funded" className="hover:text-green-400 transition-colors flex items-center gap-1">
                <Trophy className="w-3 h-3" />
                Elite Funded Trader
              </a>
              <a href="/get-funded" className="hover:text-white transition-colors">Pricing</a>
              <a href="/faq" className="hover:text-white transition-colors">FAQ</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <button 
                onClick={openCookieSettings}
                className="hover:text-white transition-colors flex items-center gap-1"
              >
                <Cookie className="w-3 h-3" />
                Manage Cookies
              </button>
            </div>
            <p className="text-gray-600 text-sm">
              © 2026 Knowledge Based Trading Ltd (Company No. 17040814) t/a Traders Capital
            </p>
            <p className="text-gray-700 text-xs mt-1">
              Registered Office: 167-169 Great Portland Street, London, England, W1W 5PF
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
